import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { QuestionMarkMinor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { collectedDataStepIconsMap } from 'src/constants/collectedData'
import { getCollectDataStepTitle } from 'src/utils/collectedData'
import { CollectedData } from 'src/interfaces/collectedData'
import { DateTime } from 'luxon'
import { ContractorStepDetailsStatusLabel } from '../contractorStepDetailsCard/components/ContractorStepDetailsStatusLabel'
import { ContractorStepDetailsCardStatus } from '../contractorStepDetailsCard/constants'

interface ContractorStepHeaderProps {
  collectedData: CollectedData
  delayed?: boolean
  blockStatus: ContractorStepDetailsCardStatus
  blockStatusLabel: string
  isDeclinedStep?: boolean
  showExpirationDate: boolean
  showEffectiveDate: boolean
  onRefreshContractors(): void | Promise<void>
}

const ContractorStepHeaderComponent = ({
  collectedData,
  delayed,
  blockStatus,
  blockStatusLabel,
  isDeclinedStep,
  showExpirationDate,
  showEffectiveDate,
}: ContractorStepHeaderProps) => {
  const expiresAt = collectedData.expiresAt || undefined

  return (
    <div>
      {delayed && (
        <Fragment>
          <div css={tw`pb-4 flex flex-row items-center`}>
            <Typography css={tw`mr-1`} variant="overline">
              DELAYED STEP
            </Typography>
            <Tooltip
              color="black"
              title={
                <Fragment>
                  <Typography css={tw`mb-2`} variant="h6" color={colors.SHADES_GREY_200}>
                    Delayed step
                  </Typography>
                  <Typography variant="body2" color={colors.SHADES_GREY_300}>
                    A step the contractor cannot complete without a prior step being approved first.
                    To initiate this step for a contractor, please approve all steps in 'Waiting for
                    Approval' tab. They will then be sent a notification with a link to continue
                    onboarding.
                  </Typography>
                </Fragment>
              }
              placement="right-end"
            >
              <span css={tw`h-5 cursor-pointer`}>
                <QuestionMarkMinor width={20} />
              </span>
            </Tooltip>
          </div>
          <div css={tw`mb-4 border-0 border-b border-solid border-[#EDEDED]`} />
        </Fragment>
      )}

      <div css={tw`flex flex-row items-start gap-2`}>
        <div css={tw`flex items-center min-w-0 flex-1`}>
          <div css={tw`flex-shrink-0`}>
            {collectedDataStepIconsMap[collectedData.collectDataStep.type]}
          </div>
          <div css={tw`flex flex-col ml-3 min-w-0`}>
            <Typography css={tw`font-semibold text-black`} variant="h6">
              {getCollectDataStepTitle(collectedData)}
            </Typography>
            <div>
              {blockStatusLabel != null && (
                <ContractorStepDetailsStatusLabel
                  label={blockStatusLabel}
                  status={blockStatus}
                  isDeclined={isDeclinedStep}
                />
              )}
            </div>
          </div>
        </div>
        {(showEffectiveDate || showExpirationDate) && (
          <div css={tw`flex flex-row gap-8 flex-shrink-0`}>
            {showEffectiveDate && (
              <div css={tw`flex flex-col`}>
                <Typography variant="body2" color={colors.SHADES_GREY_400}>
                  Effective
                </Typography>
                <Typography variant="body2" css={tw`text-black`}>
                  {DateTime.fromISO(collectedData.effectiveAt as string, { zone: 'utc' }).toFormat(
                    'MM/dd/yyyy',
                  )}
                </Typography>
              </div>
            )}
            {showExpirationDate && (
              <div css={tw`flex flex-col`}>
                <Typography variant="body2" color={colors.SHADES_GREY_400}>
                  Expiration
                </Typography>
                <Typography variant="body2" css={tw`text-black`}>
                  {expiresAt
                    ? DateTime.fromISO(expiresAt, { zone: 'utc' }).toFormat('MM/dd/yyyy')
                    : 'Not Set'}
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export const ContractorStepHeader = memo(ContractorStepHeaderComponent)
